import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';
import { renderAsync } from 'docx-preview';

const DocumentViewer: React.FC = () => {
    const { documentType, filename: encodedFilename } = useParams<{
        documentType: string;
        filename: string;
    }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [documentUrl, setDocumentUrl] = useState<string>('');
    const [isWordDoc, setIsWordDoc] = useState(false);
    const blobUrlRef = useRef<string | null>(null);
    const docxContainerRef = useRef<HTMLDivElement | null>(null);

    // Detect if this is opened in a new window/tab
    const isNewWindow = React.useMemo(() => {
        // Check if there's no browser history to go back to, or if this window was opened by another window
        return window.history.length <= 1 || window.opener !== null;
    }, []);

    const filename = encodedFilename ? decodeURIComponent(encodedFilename) : '';
    const displayName = filename.split(/[/\\]/).pop()?.replace(/\.(pdf|docx|doc)$/i, '') || filename;

    useEffect(() => {
        if (!documentType || !filename) {
            setError('Invalid document parameters');
            setLoading(false);
            return;
        }

        const fetchDocument = async () => {
            try {
                const encodedPath = encodeURIComponent(filename);
                const fileExt = filename.toLowerCase().split('.').pop();
                
                // For Office documents (.docx, .doc), use client-side docx-preview
                if (fileExt === 'docx' || fileExt === 'doc') {
                    console.log('📄 Loading Office document via client-side renderer');
                    
                    // Get the document from our API
                    const apiUrl = `/api/documents/view?filename=${encodedPath}&type=${documentType}`;
                    const response = await fetch(apiUrl, {
                        method: 'GET',
                        credentials: 'include'
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to load document: ${response.status} ${response.statusText}`);
                    }

                    const blob = await response.blob();
                    console.log('✅ Blob received, size:', blob.size, 'type:', blob.type);
                    setIsWordDoc(true);
                    setLoading(false); // Set loading to false so container renders
                    
                    // Wait for container to be rendered, then render document
                    // Use requestAnimationFrame to ensure DOM is ready
                    requestAnimationFrame(() => {
                        setTimeout(async () => {
                            if (docxContainerRef.current) {
                                console.log('📦 Container ready, rendering document...');
                                try {
                                    // Clear any existing content
                                    docxContainerRef.current.innerHTML = '';
                                    
                                    await renderAsync(blob, docxContainerRef.current, undefined, {
                                        className: 'docx-wrapper',
                                        inWrapper: true,
                                        ignoreWidth: false,
                                        ignoreHeight: false,
                                        ignoreFonts: false,
                                        breakPages: true,
                                        ignoreLastRenderedPageBreak: true,
                                        experimental: false,
                                        trimXmlDeclaration: true,
                                    });
                                    console.log('✅ Office document rendered successfully');
                                } catch (renderError) {
                                    console.error('❌ Failed to render Word document:', renderError);
                                    const errorMessage = renderError instanceof Error ? renderError.message : String(renderError);
                                    setError(`Failed to render Word document: ${errorMessage}`);
                                }
                            } else {
                                console.error('❌ Container ref is null');
                                setError('Document container not available');
                            }
                        }, 200); // Increased timeout to ensure DOM is ready
                    });
                } else {
                    // For PDFs, use direct blob display
                    console.log('📄 Loading PDF document');
                    const apiUrl = `/api/documents/view?filename=${encodedPath}&type=${documentType}`;
                    
                    const response = await fetch(apiUrl, {
                        method: 'GET',
                        credentials: 'include',
                        headers: { 'Accept': 'application/pdf' }
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to load document: ${response.status} ${response.statusText}`);
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    blobUrlRef.current = blobUrl;
                    
                    setDocumentUrl(blobUrl);
                    setLoading(false);
                    console.log('✅ PDF document loaded successfully');
                }
            } catch (err) {
                console.error('❌ Failed to load document:', err);
                setError(err instanceof Error ? err.message : 'Failed to load document');
                setLoading(false);
            }
        };

        fetchDocument();

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [documentType, filename]);

    const handleBack = () => {
        if (isNewWindow) {
            // Close the current window/tab
            window.close();
        } else {
            // Navigate back in regular browser navigation
            navigate(-1);
        }
    };

    if (error) {
        return (
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <AppBar position="static">
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleBack}
                            sx={{ mr: 2 }}
                            title={isNewWindow ? "Close window" : "Go back"}
                            aria-label={isNewWindow ? "Close window" : "Go back"}
                        >
                            {isNewWindow ? <CloseIcon /> : <ArrowBackIcon />}
                        </IconButton>
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                            Document Viewer - Error
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <AppBar position="static">
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleBack}
                        sx={{ mr: 2 }}
                        title={isNewWindow ? "Close window" : "Go back"}
                        aria-label={isNewWindow ? "Close window" : "Go back"}
                    >
                        {isNewWindow ? <CloseIcon /> : <ArrowBackIcon />}
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {displayName} ({documentType?.toUpperCase()})
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ flex: 1, position: 'relative', overflow: 'auto' }}>
                {isWordDoc ? (
                    <div 
                        ref={docxContainerRef}
                        style={{
                            width: '100%',
                            minHeight: '100%',
                            padding: '20px',
                            backgroundColor: '#ffffff',
                            fontFamily: 'Calibri, Arial, sans-serif',
                            fontSize: '11pt',
                            lineHeight: '1.15'
                        }}
                    />
                ) : (
                    <iframe
                        src={documentUrl}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title={`${displayName} - Document Viewer`}
                        onLoad={() => {
                            console.log('✅ Document loaded successfully');
                        }}
                        onError={() => {
                            console.error('❌ Failed to load document');
                            setError('Failed to load document');
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default DocumentViewer;
